import React, { useMemo, useState } from 'react';
import { AlertBanner } from '@spark';
import ObjectiveTabs from '@/features/tasks/components/Task/ObjectiveTabs';
import ClassificationOutputForm from '@/features/datasets/components/datasetUpload/steps/outputs/ClassificationOutputForm';
import EntityOutputForm from '@/features/datasets/components/datasetUpload/steps/outputs/EntityOutputForm';
import SummarizationOutputForm from '@/features/datasets/components/datasetUpload/steps/outputs/SummarizationOutputForm';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import { validatePerOutput } from '@/features/tasks/utils/validators';

/**
 * Multi-objective "Review Outputs" step.
 *
 * Renders one tab per objective in `subGroup.usecases`. Each tab hosts the
 * task-specific form (Classification / Entity / Summarization) that operates
 * on a per-objective state slice from `useUploadModalState`.
 *
 * Tab labels show a red dot when validation fails for that objective.
 * Disable the modal `Done` button while `valid` is false (`!validation.ok`).
 *
 * @param {Object} props
 * @param {Object} props.subGroup
 * @param {Object} props.perOutput - { [usecaseId]: entry }
 * @param {(usecaseId: string, patch: Object) => void} props.onPatchEntry
 * @param {Object} props.fileColumnsByUsecase - { [usecaseId]: { columns, loading, ... } }
 * @param {Function} [props.handlersByUsecase] - { [usecaseId]: { onDeleteLabel, ... } }
 */
const ReviewOutputsStep = ({
    subGroup,
    perOutput,
    onPatchEntry,
    fileColumnsByUsecase = {},
    handlersByUsecase = {},
    onValidationChange,
    onOpenHelp,
    activeUsecaseId: controlledActiveUsecaseId,
    onActiveUsecaseChange,
}) => {
    const usecases = subGroup?.usecases || [];
    // Fallback internal state so the component still works uncontrolled. When the
    // parent passes `activeUsecaseId`/`onActiveUsecaseChange`, those drive the tabs
    // (the modal footer needs to know which sub-task is active to gate "Done").
    const [internalActiveUsecaseId, setInternalActiveUsecaseId] = useState(
        usecases[0]?._id != null ? String(usecases[0]._id) : null
    );
    const isControlled = onActiveUsecaseChange != null;
    const activeUsecaseId = isControlled
        ? controlledActiveUsecaseId
        : internalActiveUsecaseId;
    const setActiveUsecaseId = isControlled
        ? onActiveUsecaseChange
        : setInternalActiveUsecaseId;

    const validation = useMemo(
        () => validatePerOutput(perOutput, usecases),
        [perOutput, usecases]
    );

    React.useEffect(() => {
        onValidationChange?.(validation);
    }, [validation, onValidationChange]);

    const errorByUsecaseId = validation.errors;

    const renderForm = (usecase) => {
        const ucId = String(usecase._id);
        const entry = perOutput[ucId] || {};
        const onPatch = (patch) => onPatchEntry(ucId, patch);
        const fileState = fileColumnsByUsecase[ucId] || {};
        const handlers = handlersByUsecase[ucId] || {};

        if (usecase.task_type === TaskType.classification) {
            return (
                <ClassificationOutputForm
                    entry={entry}
                    onPatch={onPatch}
                    columns={fileState.columns || []}
                    columnsLoading={!!fileState.columnsLoading}
                    isMultilabel={!!usecase.is_multilabel}
                    extractedLabels={fileState.extractedLabels || []}
                    labelsLoading={!!fileState.labelsLoading}
                    labelExamples={fileState.labelExamples || {}}
                    availableLabels={fileState.availableLabels || []}
                    labelError={errorByUsecaseId[ucId]}
                    onDeleteLabel={handlers.onDeleteLabel}
                    onReplaceLabel={handlers.onReplaceLabel}
                    onAddNewLabel={handlers.onAddNewLabel}
                    clientId={usecase.client_id}
                    usecaseId={usecase._id}
                    getExamples={fileState.getExamples}
                    confidenceRejectionEnabled={usecase.confidence_rejection_enabled === true}
                />
            );
        }
        if (
            usecase.task_type === TaskType.NER ||
            usecase.task_type === TaskType.NER_WITH_RELATIONS ||
            usecase.task_type === 'EntityRecognitionResponse' ||
            usecase.task_type === 'NERResponse'
        ) {
            return (
                <EntityOutputForm
                    entry={entry}
                    onPatch={onPatch}
                    columns={fileState.columns || []}
                    columnsLoading={!!fileState.columnsLoading}
                    withRelations={usecase.task_type === TaskType.NER_WITH_RELATIONS}
                    extractedLabels={fileState.extractedLabels || []}
                    labelsLoading={!!fileState.labelsLoading}
                    labelExamples={fileState.labelExamples || {}}
                    availableLabels={fileState.availableLabels || []}
                    extractedRelations={fileState.extractedRelations || []}
                    relationsLoading={!!fileState.relationsLoading}
                    labelError={errorByUsecaseId[ucId]}
                    onDeleteLabel={handlers.onDeleteLabel}
                    onReplaceLabel={handlers.onReplaceLabel}
                    onAddNewLabel={handlers.onAddNewLabel}
                    onDeleteRelation={handlers.onDeleteRelation}
                    onReplaceRelation={handlers.onReplaceRelation}
                    onAddNewRelation={handlers.onAddNewRelation}
                />
            );
        }
        if (isGenerativeTask(usecase.task_type)) {
            return (
                <SummarizationOutputForm
                    entry={entry}
                    onPatch={onPatch}
                    columns={fileState.columns || []}
                    columnsLoading={!!fileState.columnsLoading}
                    extractedSummary={fileState.extractedSummary}
                    summaryLoading={!!fileState.summaryLoading}
                    onOpenHelp={onOpenHelp}
                    labelError={errorByUsecaseId[ucId]}
                    taskType={usecase.task_type}
                />
            );
        }
        return (
            <AlertBanner
                feedback="warning"
                text={`Unsupported objective type: ${usecase.task_type}`}
                showButton={false}
                showCloseButton={false}
            />
        );
    };

    if (usecases.length === 0) {
        return (
            <AlertBanner
                feedback="warning"
                text="No objectives to configure for this task."
                showButton={false}
                showCloseButton={false}
            />
        );
    }

    return (
        <div>
            <ObjectiveTabs
                usecases={usecases}
                activeUsecaseId={activeUsecaseId}
                onChange={setActiveUsecaseId}
                scrollable
                errorByUsecaseId={Object.fromEntries(
                    Object.keys(errorByUsecaseId).map((k) => [k, true])
                )}
                renderPanel={(usecase) => (
                    <div
                        style={{
                            padding: 0,
                            borderRadius: 8,
                            background: '#FFFFFF',
                        }}
                    >
                        {renderForm(usecase)}
                    </div>
                )}
            />
        </div>
    );
};

export default ReviewOutputsStep;
